import { useState } from "preact/hooks"
import { Link } from "wouter-preact"
import { ChevronDown, ChevronRight, ClipboardList } from "lucide-react"

import type { PurchaseOrder } from "@/api/purchase_order.tsx"
import { handleViewS3Uri } from "@/deduction/[id]/UserFiles.tsx"
import { PurchaseOrderFacts, PurchaseOrderLines } from "@/purchase_orders/PurchaseOrderLines.tsx"
import { format_day, format_units, terms_label } from "@/purchase_orders/purchase_orders_display.ts"
import { exactCurrencyFormatter } from "@/utils"

function OrderBlock({ order }: { order: PurchaseOrder }) {
  const [open, setOpen] = useState(false)
  const Chevron = open ? ChevronDown : ChevronRight
  const terms = terms_label(order)
  return (
    <div class="border-t first:border-t-0 py-2">
      <button type="button" class="w-full flex items-start gap-2 text-left cursor-pointer" onClick={() => setOpen(!open)}>
        <Chevron className="h-4 w-4 mt-0.5 shrink-0 text-slate-500" />
        <div class="min-w-0">
          <div class="text-sm font-medium">
            PO <span class="font-mono">{order.po_number}</span> · {order.customer_name}
          </div>
          <div class="text-xs text-slate-600">
            Ordered {format_day(order.po_date)} · due {format_day(order.requested_date)} · {order.line_count} line{order.line_count === 1 ? "" : "s"},{" "}
            {format_units(order.total_quantity)} units, {order.total_amount !== null ? exactCurrencyFormatter(order.total_amount) : "—"}
            {terms ? ` · ${terms}` : ""}
          </div>
        </div>
      </button>
      {open && (
        <div class="mt-2 pl-6 flex flex-col gap-3">
          <PurchaseOrderFacts order={order} />
          <PurchaseOrderLines order={order} />
          {order.s3_uri && (
            <a href="#" class="text-xs text-blue-700 hover:underline" onClick={(e: MouseEvent) => handleViewS3Uri(order.s3_uri!)(e)}>
              Open the customer's original order document
            </a>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * The customer's own purchase order(s) behind a deduction, found by PO number: what was ordered,
 * at what price, due when. Shown only when an order is on file.
 */
export function PurchaseOrderPanel({ orders, className }: { orders: PurchaseOrder[]; className?: string }) {
  if (orders.length === 0) {
    return null
  }
  return (
    <div class={`rounded-md border bg-white px-3 py-2 ${className ?? ""}`}>
      <div class="flex items-center justify-between gap-2">
        <div class="flex items-center gap-2 text-sm font-semibold">
          <ClipboardList className="h-4 w-4 text-slate-500" />
          Customer purchase order{orders.length === 1 ? "" : "s"}
        </div>
        <Link href="/purchase-orders" class="text-xs text-blue-700 hover:underline">
          All purchase orders
        </Link>
      </div>
      {orders.map((order: PurchaseOrder) => (
        <OrderBlock key={order.id} order={order} />
      ))}
    </div>
  )
}
